import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../hooks/UserContext";
import { API } from "../../../api";
import type { Branch, Company, Contact, Deal, Grower, Job } from "../../../api/types";
import { ImportOption, ImportOptionChoices } from "../../../utilities/types";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { INVALID_USERCONTEXT } from "../../../utilities/constants";
import { Oops } from "../../../utilities/oops";
import { ImportFormViewModel, JobSubmissionFormProps } from "./ImportForm.types";
import { getImportOptions, sortContacts, sortDeals } from "./ImportForm.helpers";
import type { FeatureStats } from "../ImportTable/ImportTable.types";

// oxlint-disable-next-line no-unused-vars
export function useImportFormViewModel(props: JobSubmissionFormProps): ImportFormViewModel {
	const userContext = useContext(UserContext);
	const [user, setUser] = useState<Contact>();

	const [deals, setDeals] = useState<Deal[]>([]);
	const [selectedDeals, setSelectedDeals] = useState<Deal[]>([]);
	const [growers, setGrowers] = useState<Grower[]>([]);
	const [, setSelectedGrowers] = useState<Grower[]>([]);
	const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
	const [salespersons, setSalespersons] = useState<Contact[]>([]);
	const [loading, setLoading] = useState(false);
	const [isZone, setIsZone] = useState(false);
	const [importOptions, setImportOptions] = useState<ImportOption[]>([]);
	const [dataImportOption, setDataImportOption] = useState<ImportOption | null>(null);
	const [lastCropOptions, setLastCropOptions] = useState<string[]>([]);
	const [nextCropOptions, setNextCropOptions] = useState<string[]>([]);

	const [, setSelectableFiles] = useState<File[]>([]);

	// Modal state for each import type
	const [geoDataModalOpen, setGeoDataModalOpen] = useState(false);
	const [proagricaModalOpen, setProagricaModalOpen] = useState(false);
	const [agrianModalOpen, setAgrianModalOpen] = useState(false);

	// Track whether each modal has been used (opened at least once)
	const [geoDataModalUsed, setGeoDataModalUsed] = useState(false);
	const [proagricaModalUsed, setProagricaModalUsed] = useState(false);
	const [agrianModalUsed, setAgrianModalUsed] = useState(false);

	// Jobs extracted from modal, displayed in ImportTable on main page
	const [extractedJobs, setExtractedJobs] = useState<Job[]>([]);
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);
	const [activeImportType, setActiveImportType] = useState<ImportOption | null>(null);

	// Key to force remount of form components (for clearing state after submit)
	const [formKey, setFormKey] = useState(0);

	// Handler for when jobs are extracted from a modal form
	const handleJobsExtracted = (jobs: Job[], importType: ImportOption, stats?: FeatureStats) => {
		setExtractedJobs(jobs);
		setFeatureStats(stats);
		setActiveImportType(importType);
		// Close the appropriate modal
		if (importType.id === ImportOptionChoices.BulkUpload2.id) {
			setGeoDataModalOpen(false);
		}
		if (importType.id === ImportOptionChoices.Proagrica.id) setProagricaModalOpen(false);
		if (importType.id === ImportOptionChoices.Agrian.id) setAgrianModalOpen(false);
	};

	// Clear extracted jobs (e.g., when user cancels or starts over)
	const handleClearExtractedJobs = () => {
		setExtractedJobs([]);
		setFeatureStats(undefined);
		setActiveImportType(null);
	};

	// Reset everything after successful job submission
	const handleResetAfterSubmit = () => {
		handleClearExtractedJobs();
		// Clear modal used flags
		setGeoDataModalUsed(false);
		setProagricaModalUsed(false);
		setAgrianModalUsed(false);
		// Increment formKey to force remount and clear form states
		setFormKey((k) => k + 1);
	};

	useEffect(() => {
		for (const deal of deals) {
			if (selectedDeals.includes(deal)) {
				if (deal.type === "Zone") {
					setIsZone(true);
				} else {
					setIsZone(false);
				}
				break;
			}
		}
	}, []);

	useEffect(() => {
		// React advises to declare the async function directly inside useEffect
		async function getData() {
			try {
				if (userContext == undefined || !userContext.id) {
					Sentry.captureMessage("userContext is undefined: JobSubmissionForm.tsx", "info");
					toast.error(INVALID_USERCONTEXT);
					return;
				}
				const user = await API.Data.Contacts.get.byId(userContext.id);
				setUser(user);
				const importOpts = await getImportOptions(user, selectedCompany, selectedBranches);
				const companyRecords = await API.Data.Companies.get.byContact(user);

				setImportOptions(importOpts);
				if (importOpts.length === 1) {
					setDataImportOption(importOpts[0]);
				}
				// sort in place by company name
				companyRecords.sort((companyA, companyB) => {
					if (companyA.name === "Rogo AG" || companyA.name === "ROGO Software") return -1;
					if (companyB.name === "Rogo AG" || companyB.name === "ROGO Software") return 1;
					return companyA.name.localeCompare(companyB.name);
				});
				setCompanyOptions(companyRecords);
				if (companyRecords.length === 1) {
					const companyRecord = companyRecords[0];
					setSelectedCompany(companyRecord);
					const growers = await API.Data.Growers.get.byCompany(companyRecord);
					const branches = await API.Data.Branches.get.byCompany(companyRecord);
					const deals = await API.Data.Deals.get.byCompany(companyRecord);
					sortDeals(deals);
					setDeals(deals);
					setGrowers(growers);
					setBranches(branches);
				}
				setLastCropOptions(await API.Data.Jobs.get.lastCropOptions());
				setNextCropOptions(await API.Data.Jobs.get.nextCropOptions());
				setLoading(false);
			} catch (error) {
				Oops.handleError(error, "SubmitJobs.JobSubmissionForm.getData");
			}
		}

		// You need to restrict it at some point
		// This is just dummy code and should be replaced by actual
		if (!deals.length) {
			setLoading(true);
			getData();
		}
	}, [userContext]);

	async function onCompanyChanged(company: Company | null) {
		setLoading(true);
		if (company) {
			// Order may be important if things are asynchronously rerendering...
			const branches = await API.Data.Branches.get.byCompany(company);
			setSelectedBranches([]);
			setBranches(branches);

			const growers = await API.Data.Growers.get.byCompany(company);
			setSelectedGrowers([]);
			setGrowers(growers);

			const deals = await API.Data.Deals.get.byCompany(company);
			setSelectedDeals([]);
			setDeals(deals);

			const salespersons = await getSalespersons(company, branches);
			setSalespersons(salespersons);

			setSelectedCompany(company);
		}
		setLoading(false);
	}

	async function getSalespersons(company: Company, branches: Branch[]) {
		const contacts = await API.Data.Contacts.get.byCompany(company);
		contacts.sort((a, b) => a.name.localeCompare(b.name));
		return sortContacts(contacts, branches);
	}

	return {
		// State
		userContext,
		user,
		deals,
		selectedDeals,
		growers,
		companyOptions,
		branches,
		selectedBranches,
		selectedCompany,
		salespersons,
		loading,
		isZone,
		importOptions,
		dataImportOption,
		lastCropOptions,
		nextCropOptions,

		// Modal state
		geoDataModalOpen,
		proagricaModalOpen,
		agrianModalOpen,
		geoDataModalUsed,
		proagricaModalUsed,
		agrianModalUsed,
		extractedJobs,
		featureStats,
		activeImportType,
		formKey,

		// Handlers
		onCompanyChanged,
		setSelectedDeals,
		setSelectedBranches,
		setDataImportOption,
		setSelectableFiles,
		setLoading,

		// Modal handlers
		setGeoDataModalOpen,
		setProagricaModalOpen,
		setAgrianModalOpen,
		setGeoDataModalUsed,
		setProagricaModalUsed,
		setAgrianModalUsed,
		handleJobsExtracted,
		handleClearExtractedJobs,
		setExtractedJobs,
		handleResetAfterSubmit,
	};
}
